import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { ToastMessage } from '@/components'
import { useGetExternalBankAccounts } from '../../hooks/use-get-external-bank-accounts'
import { usePublicToken } from '../../hooks/use-public-token'

const MAX_POLLING_DURATION = 60000 // 60 seconds
const POLLING_INTERVAL = 5000 // 5 seconds

export const usePaymentMethods = (
  { fetchOnMount, monitor, onAccountLinked } = {
    fetchOnMount: false,
    setIsPaymentMethodAdding: () => {},
    monitor: true, // Accounts should be monitored by default
    onAccountLinked: null, // Optional callback when account is linked
  }
) => {
  const [startPolling, setStartPolling] = useState(false)
  const [newAccountId, setNewAccountId] = useState(null)
  const [isPaymentMethodAdding, setIsPaymentMethodAdding] = useState(false)
  const startTimeRef = useRef(null)

  // Fetch the current payment methods
  const {
    data: { data: externalBankAccountData } = {},
    isFetched: isExternalBankAccountDataFetched,
    isError: isExternalBankAccountDataError,
    isLoading: isExternalBankAccountDataLoading,
    refetch: refetchExternalBankAccountData,
  } = useGetExternalBankAccounts(fetchOnMount)

  // Function to connect a non-monitored bank account
  const { mutateAsync } = usePublicToken({ monitor })

  async function publicTokenMutate(payload) {
    try {
      setIsPaymentMethodAdding(true)
      const { financialAccountId, isPendingVerification, institutionName, loanLeaseDetection } = await mutateAsync(payload)
      
      if (isPendingVerification) {
        // Don't start polling for a pending verification account
        // There's no need - it's not connected to Highnote yet
        setIsPaymentMethodAdding(false)
        refetchExternalBankAccountData() // Refetch to show pending account immediately
        const name = institutionName ? `"${institutionName}"` : 'Account'
        toast.custom(() => (
          <ToastMessage
            success
            text={`${name} added successfully`}
            description="Verification is still in progress. This account cannot be used until confirmed."
          />
        ))
        return { financialAccountId: null, loanLeaseDetection: null }
      } else if (financialAccountId) {
        setNewAccountId(financialAccountId)
        setStartPolling(true)
        startTimeRef.current = Date.now()
      } else {
        throw new Error('Financial account ID not contained in response')
      }
      
      // Return detection result if available
      return { financialAccountId, loanLeaseDetection: loanLeaseDetection || null }
    } catch (error) {
      setIsPaymentMethodAdding(false)
      throw error
    }
  }

  // Polling effect
  useEffect(() => {
    if (!startPolling || !startTimeRef.current) return

    const interval = setInterval(() => {
      const elapsedTime = Date.now() - startTimeRef.current

      if (elapsedTime >= MAX_POLLING_DURATION) {
        setStartPolling(false) // Stop polling after max duration
        setIsPaymentMethodAdding(false)
        clearInterval(interval)

        toast.custom(() => (
          <ToastMessage text="This is taking longer than expected. Your payment method may take a few moments to appear." />
        ))
        return
      }

      refetchExternalBankAccountData()
    }, POLLING_INTERVAL)

    return () => clearInterval(interval) // Cleanup on unmount
  }, [startPolling, refetchExternalBankAccountData])

  // Stop polling if new account is received
  useEffect(() => {
    if (startPolling) {
      const accountReceived = externalBankAccountData?.find(account => account?.id === newAccountId)
      // setStartPolling(!accountReceived)
      if (accountReceived) {
        setStartPolling(false)
        setIsPaymentMethodAdding(false)
        
        // Call callback if provided, otherwise show default toast
        if (onAccountLinked) {
          onAccountLinked(accountReceived)
        } else {
          toast.custom(() => (
            <ToastMessage
              success
              text="Account added successfully"
              description="Your new account is now active and can be used both to pay your balance and as a default payment method."
            />
          ))
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalBankAccountData, onAccountLinked])

  return {
    externalBankAccountData,
    isExternalBankAccountDataLoading,
    isExternalBankAccountDataFetched,
    isExternalBankAccountDataError,
    publicTokenMutate,
    isPaymentMethodAdding,
  }
}
